import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../../../lib/supabaseClient'
import { useAuth } from '../../../contexts/AuthContext'

// Fetch the active spy_locations pool once. Global shared content, so
// no realtime — a refetch() is exposed for the Phase-2 AI generator /
// manual refresh. Each row carries bilingual name + parallel role
// arrays (roles_en[i] ↔ roles_ar[i]).
export function useSpyLocations() {
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: e } = await supabase
      .from('spy_locations')
      .select('id, name_en, name_ar, roles_en, roles_ar, category')
      .eq('active', true)
    if (e) setError(e.message)
    setLocations(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { refetch() }, [refetch])

  return { locations, loading, error, refetch }
}

// Log a finished round. `outcome` is one of spy_caught | spy_guessed
// | spy_escaped. Fire-and-forget from the caller's perspective.
export function useLogSpyResult() {
  const { user } = useAuth()
  return useCallback(async ({ locationId, locationName, players, spyNames, outcome }) => {
    if (!user) return false
    const { error } = await supabase.from('spy_results').insert({
      location_id: locationId || null,
      location_name: locationName || '',
      players,
      spy_names: spyNames,
      outcome,
      recorded_by: user.id,
    })
    return !error
  }, [user])
}

// Aggregate the shared results history into a per-name scoreboard.
// Names are normalized (trim + lowercase) so "عمر" and " عمر " merge,
// but the display keeps the first-seen spelling.
export function useSpyScoreboard() {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('spy_results')
      .select('location_name, players, spy_names, outcome, played_at')
      .order('played_at', { ascending: false })
      .limit(500)
    setResults(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { refetch() }, [refetch])

  const scoreboard = useMemo(() => {
    const byName = new Map()
    const ensure = (name) => {
      const key = name.trim().toLowerCase()
      if (!key) return null
      let e = byName.get(key)
      if (!e) {
        e = { name: name.trim(), games: 0, timesSpy: 0, caught: 0, spyWins: 0, groupWins: 0 }
        byName.set(key, e)
      }
      return e
    }
    for (const r of results) {
      const spies = new Set((r.spy_names || []).map((s) => s.trim().toLowerCase()))
      const spyWon = r.outcome === 'spy_guessed' || r.outcome === 'spy_escaped'
      for (const p of r.players || []) {
        const e = ensure(p)
        if (!e) continue
        e.games += 1
        const isSpy = spies.has(p.trim().toLowerCase())
        if (isSpy) {
          e.timesSpy += 1
          if (r.outcome === 'spy_caught') e.caught += 1
          if (spyWon) e.spyWins += 1
        } else if (r.outcome === 'spy_caught') {
          e.groupWins += 1
        }
      }
    }
    const rows = Array.from(byName.values())
    rows.sort((a, b) => (b.spyWins - a.spyWins) || (b.games - a.games))
    return rows
  }, [results])

  return { results, scoreboard, loading, refetch }
}
